import React, { useMemo, useRef, useState } from "react";
import axios from "axios";
import { message, Modal } from "antd";
import { saveAs } from "file-saver";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import { ArrowLeft, ClipboardCheck, FileArchive, Settings, Upload } from "lucide-react";

import { getAdminAuthHeaders } from "../api/adminAuth";
import styles from "./DocumentArchivePage.module.css";

const DOCUMENT_ARCHIVE_ENDPOINT = "/api/documents/archive";
const DOCUMENT_TEMPLATES_ENDPOINT = "/api/documents/templates";
const DOCUMENT_TEMPLATE_TAGS_ENDPOINT = "/api/documents/template-tags";

const withAdminAuth = (config = {}) => ({
    ...config,
    headers: {
        ...(config.headers || {}),
        ...getAdminAuthHeaders()
    }
});

const getTemplateEndpoint = (code) => `${DOCUMENT_TEMPLATES_ENDPOINT}/${encodeURIComponent(code)}`;
const getTemplateDownloadEndpoint = (code) => `${getTemplateEndpoint(code)}/download`;
const getTemplateTagsEndpoint = (code) => `${getTemplateEndpoint(code)}/tags`;

const REQUIRED_INPUT_FIELDS = [
    "№заявки",
    "дата заявки",
    "ГК",
    "дата ГК",
    "ИГК",
    "ОБС клиента",
    "ОБС СПЦ",
    "сумма",
    "прибыль",
    "дата контракта",
    "номер контракта"
];

const INPUT_FIELD_GROUPS = [
    {
        title: "Заявка",
        fields: ["№заявки", "дата заявки"]
    },
    {
        title: "Госконтракт",
        fields: ["ГК", "дата ГК", "ИГК"]
    },
    {
        title: "Контракт",
        fields: ["номер контракта", "дата контракта"]
    },
    {
        title: "Счета",
        fields: ["ОБС клиента", "ОБС СПЦ"]
    },
    {
        title: "Финансы",
        fields: ["сумма", "прибыль"]
    }
];

const COMMON_TAG_MAPPING = REQUIRED_INPUT_FIELDS.map(field => ({
    inputField: field,
    tag: `{{${field}}}`
}));

const REQUIRED_TABLE_FIELDS = [
    "№ п/п",
    "Наименование товара (работы, услуги)",
    "Артикул",
    "Производитель",
    "Количество",
    "Ед. изм.",
    "Цена без НДС",
    "Цена с НДС",
    "Сумма без НДС",
    "Сумма с НДС",
    "Прогноз поставки"
];

const normalize = (value) => String(value ?? "").trim().replace(/\s+/g, " ").toLowerCase();

const DATE_FIELDS = new Set(["дата заявки", "дата ГК", "дата контракта", "Прогноз поставки"].map(normalize));
const isDateField = (field) => DATE_FIELDS.has(normalize(field)) || normalize(field).includes("дата");

const padDatePart = (value) => String(value).padStart(2, "0");

const formatDate = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
    return `${padDatePart(date.getDate())}.${padDatePart(date.getMonth() + 1)}.${date.getFullYear()}`;
};

const excelSerialToDate = (value) => {
    const date = XLSX.SSF.parse_date_code(value);
    if (!date) return null;
    return new Date(date.y, date.m - 1, date.d);
};

const timestampToDate = (value) => {
    const normalizedValue = typeof value === "number" && value < 100000000000 ? value * 1000 : value;
    const date = new Date(normalizedValue);
    return Number.isNaN(date.getTime()) ? null : date;
};

const parseDateString = (value) => {
    const source = String(value || "").trim();
    const dottedDate = source.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
    if (dottedDate) {
        return new Date(Number(dottedDate[3]), Number(dottedDate[2]) - 1, Number(dottedDate[1]));
    }

    const isoDate = source.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoDate) {
        return new Date(Number(isoDate[1]), Number(isoDate[2]) - 1, Number(isoDate[3]));
    }

    return null;
};

const formatDateCellValue = (value) => {
    if (value instanceof Date) return formatDate(value);

    if (typeof value === "number") {
        if (value > 20000 && value < 80000) return formatDate(excelSerialToDate(value));
        if (value > 1000000000) return formatDate(timestampToDate(value));
    }

    if (typeof value === "string") {
        const date = parseDateString(value);
        return date ? formatDate(date) : value;
    }

    return value;
};

const formatCellValue = (value, field) => {
    if (value === null || value === undefined) return "";
    if (value instanceof Date) return formatDate(value);
    if (isDateField(field)) return formatDateCellValue(value);
    return value;
};

const toDateInputValue = (value) => {
    const date = parseDateString(value);
    if (!date) return "";
    return `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(date.getDate())}`;
};

const fromDateInputValue = (value) => {
    const date = parseDateString(value);
    return date ? formatDate(date) : "";
};

const makeSafeFileName = (value) => {
    const baseName = String(value || "documents").trim().replace(/[\\/:*?"<>|]+/g, "_");
    return `${baseName || "documents"}.zip`;
};

const getFileNameFromDisposition = (contentDisposition, fallbackName) => {
    const fileNameMatch = String(contentDisposition || "").match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i);
    return fileNameMatch ? decodeURIComponent(fileNameMatch[1]) : fallbackName;
};

const isBlank = (value) => value === null || value === undefined || String(value).trim() === "";

const getEmptyInputFields = (input, skippedFields = [], requiredFields = REQUIRED_INPUT_FIELDS) => {
    const skippedSet = new Set(skippedFields.map(normalize));
    return requiredFields.filter(field => !skippedSet.has(normalize(field)) && isBlank(input[field]));
};

const stripTagBraces = (tag) => String(tag || "").replace(/^\{\{/, "").replace(/\}\}$/, "");
const getRepositoryTag = (item) => {
    if (typeof item === "string") return item;
    return item?.tag?.tag ||
        item?.templateTag?.tag ||
        item?.tag ||
        item?.tagName ||
        item?.templateTag ||
        item?.name ||
        item?.value ||
        "";
};
const getRepositoryField = (item) =>
    item?.tag?.inputField ||
    item?.templateTag?.inputField ||
    item?.inputField ||
    item?.jsonField ||
    item?.fieldName ||
    item?.field ||
    item?.inputKey ||
    item?.inputPath ||
    item?.path ||
    stripTagBraces(getRepositoryTag(item));

const getTagForInputField = (field, repository) => {
    const repositoryTag = repository.find(item => normalize(getRepositoryField(item)) === normalize(field));
    return getRepositoryTag(repositoryTag) || `{{${field}}}`;
};

const getTemplateDescription = (template) =>
    template?.description || template?.fileName || template?.code || "Шаблон";

const normalizeTemplateTagsResponse = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.fields)) return data.fields;
    if (Array.isArray(data?.tags)) return data.tags;
    if (Array.isArray(data?.templateTags)) return data.templateTags;
    if (Array.isArray(data?.knownTags)) return data.knownTags;
    return [];
};

const TABLE_INPUT_FIELDS = new Set(["items", "item", "table", "таблица", "табличная часть"].map(normalize));
const CALCULATED_INPUT_FIELDS = new Set(["ндс", "НДС", "сумма прописью", "ндс прописью", "НДС прописью"].map(normalize));

const FIELD_ALIASES = new Map([
    ["сумма или сумма items[].сумма с ндс", "сумма"],
    ["сумма items[].сумма с ндс", "сумма"],
    ["items[].сумма с ндс", "сумма"]
].map(([from, to]) => [normalize(from), to]));

const getCanonicalInputField = (field) => {
    const normalizedField = normalize(field);
    if (!normalizedField || TABLE_INPUT_FIELDS.has(normalizedField) || CALCULATED_INPUT_FIELDS.has(normalizedField)) return "";
    if (FIELD_ALIASES.has(normalizedField)) return FIELD_ALIASES.get(normalizedField);
    return REQUIRED_INPUT_FIELDS.find(item => normalize(item) === normalizedField) || stripTagBraces(field).trim();
};

const getInputFieldsFromTemplateTags = (tags) => {
    const fields = tags
        .map(getRepositoryField)
        .map(getCanonicalInputField)
        .filter(Boolean);

    return [...new Set(fields)];
};

const getFieldHint = (field) => {
    if (normalize(field) === "сумма") {
        return "НДС, НДС прописью и сумма прописью проставятся автоматически";
    }

    return "";
};

const buildTagChecksFromRepository = (codes, repository) => {
    const knownTags = repository.map(getRepositoryTag).filter(Boolean);
    const knownTagSet = new Set(knownTags);
    const missingCommonTags = COMMON_TAG_MAPPING
        .map(item => item.tag)
        .filter(tag => !knownTagSet.has(tag));

    return codes.map(code => ({
        templateCode: code,
        knownTags,
        unknownTags: [],
        missingCommonTags
    }));
};

const validateWorkbook = async (file) => {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, {
        type: "array",
        cellDates: true
    });

    const errors = [];
    const sheetNames = workbook.SheetNames;
    const firstSheetName = sheetNames[0];
    const secondSheetName = sheetNames[1];

    if (!firstSheetName) errors.push("В файле нет первой закладки.");

    if (errors.length > 0) {
        return { valid: false, errors, payload: null };
    }

    const firstRows = XLSX.utils.sheet_to_json(workbook.Sheets[firstSheetName], {
        header: 1,
        defval: null,
        blankrows: false
    });
    const secondRows = secondSheetName
        ? XLSX.utils.sheet_to_json(workbook.Sheets[secondSheetName], {
            header: 1,
            defval: null,
            blankrows: false
        })
        : [];

    const inputMap = new Map(
        firstRows
            .filter(row => row.some(cell => cell !== null && cell !== ""))
            .map(row => [normalize(row[0]), formatCellValue(row[1], row[0])])
    );

    const missingInputFields = REQUIRED_INPUT_FIELDS.filter(field => !inputMap.has(normalize(field)));

    const headerRowIndex = secondRows.findIndex(row => {
        const normalizedRow = row.map(normalize);
        return REQUIRED_TABLE_FIELDS.every(field => normalizedRow.includes(normalize(field)));
    });

    const tableWarnings = [];
    let items = [];

    if (!secondSheetName) {
        tableWarnings.push("В файле нет второй закладки. Табличная часть не будет вставлена в документ.");
    } else if (headerRowIndex === -1) {
        tableWarnings.push(`Во второй закладке не найдена строка заголовков. Табличная часть не будет вставлена в документ.`);
    } else {
        const headerRow = secondRows[headerRowIndex];
        const headerIndexByName = new Map(headerRow.map((header, index) => [normalize(header), index]));
        items = secondRows
            .slice(headerRowIndex + 1)
            .filter(row => row.some(cell => cell !== null && cell !== ""))
            .map(row => {
                const item = {};
                REQUIRED_TABLE_FIELDS.forEach(field => {
                    item[field] = formatCellValue(row[headerIndexByName.get(normalize(field))], field);
                });
                return item;
            })
            .filter(item => Object.values(item).some(value => value !== null && value !== ""));

        if (items.length === 0) {
            tableWarnings.push("Во второй закладке нет строк табличной части. Табличная часть не будет вставлена в документ.");
        }
    }

    const input = {};
    REQUIRED_INPUT_FIELDS.forEach(field => {
        input[field] = inputMap.has(normalize(field)) ? inputMap.get(normalize(field)) : "";
    });

    const emptyInputFields = getEmptyInputFields(input);
    const warnings = [
        ...(missingInputFields.length > 0
            ? [`В первой закладке не найдены поля: ${missingInputFields.join(", ")}. Заполните их вручную ниже.`]
            : []),
        ...tableWarnings
    ];

    return {
        valid: true,
        errors: [],
        warnings,
        missingInputFields,
        tableMissing: tableWarnings.length > 0,
        emptyInputFields,
        payload: {
            fileName: file.name,
            sheets: {
                input: firstSheetName,
                items: secondSheetName
            },
            input,
            items
        }
    };
};

export default function DocumentArchivePage({ embedded = false }) {
    const [selectedFile, setSelectedFile] = useState(null);
    const [validation, setValidation] = useState(null);
    const [skippedInputFields, setSkippedInputFields] = useState([]);
    const [sending, setSending] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [templatesLoading, setTemplatesLoading] = useState(false);
    const [templateCodes, setTemplateCodes] = useState([]);
    const [newTemplateCode, setNewTemplateCode] = useState("");
    const [templateFile, setTemplateFile] = useState(null);
    const [templateUploading, setTemplateUploading] = useState(false);
    const [templateRenameDrafts, setTemplateRenameDrafts] = useState({});
    const [templateDescriptionDrafts, setTemplateDescriptionDrafts] = useState({});
    const [templateActionCode, setTemplateActionCode] = useState("");
    const [templateDownloadingCode, setTemplateDownloadingCode] = useState("");
    const [templateTagsLoading, setTemplateTagsLoading] = useState(false);
    const [templateTagChecks, setTemplateTagChecks] = useState([]);
    const [templateTagsByCode, setTemplateTagsByCode] = useState({});
    const [selectedTemplateTagsLoading, setSelectedTemplateTagsLoading] = useState(false);
    const [templateSettingsOpen, setTemplateSettingsOpen] = useState(false);
    const [showTagInfo, setShowTagInfo] = useState(false);
    const [tagRepository, setTagRepository] = useState([]);
    const [tagRepositoryLoading, setTagRepositoryLoading] = useState(false);
    const [tagSaving, setTagSaving] = useState(false);
    const [newTag, setNewTag] = useState("");
    const [newTagInputField, setNewTagInputField] = useState("");
    const [selectedRepositoryTag, setSelectedRepositoryTag] = useState("");
    const fileInputRef = useRef(null);
    const templateFileInputRef = useRef(null);

    const summary = useMemo(() => {
        if (!validation?.payload) return null;

        return {
            requestNumber: validation.payload.input["№заявки"] || "не заполнено",
            itemsCount: validation.payload.items.length,
            firstSheet: validation.payload.sheets.input,
            secondSheet: validation.payload.sheets.items
        };
    }, [validation]);

    const selectedTemplates = useMemo(() => {
        return templateCodes.map(code => templates.find(template => template.code === code) || {
            code,
            description: "Выбранный шаблон"
        });
    }, [templateCodes, templates]);

    const selectedTemplateFields = useMemo(() => {
        if (templateCodes.length === 0) return [];

        const fields = templateCodes.flatMap(code => getInputFieldsFromTemplateTags(templateTagsByCode[code] || []));
        return [...new Set(fields)];
    }, [templateCodes, templateTagsByCode]);

    const groupedSelectedFields = useMemo(() => {
        const groupedFieldSet = new Set(INPUT_FIELD_GROUPS.flatMap(group => group.fields).map(normalize));
        const baseGroups = INPUT_FIELD_GROUPS
            .map(group => ({
                ...group,
                fields: group.fields.filter(field => selectedTemplateFields.includes(field))
            }))
            .filter(group => group.fields.length > 0);

        const otherFields = selectedTemplateFields.filter(field => !groupedFieldSet.has(normalize(field)));
        return otherFields.length > 0
            ? [...baseGroups, { title: "Поля шаблона", fields: otherFields }]
            : baseGroups;
    }, [selectedTemplateFields]);

    const currentEmptyInputFields = useMemo(() => {
        if (!validation?.payload) return validation?.emptyInputFields || [];
        return getEmptyInputFields(validation.payload.input, skippedInputFields, selectedTemplateFields);
    }, [skippedInputFields, selectedTemplateFields, validation]);

    const currentMissingInputFields = useMemo(() => {
        if (!validation?.missingInputFields?.length) return [];
        return validation.missingInputFields.filter(field => selectedTemplateFields.includes(field));
    }, [selectedTemplateFields, validation]);

    const loadTemplates = async () => {
        setTemplatesLoading(true);
        try {
            const response = await axios.get(DOCUMENT_TEMPLATES_ENDPOINT, withAdminAuth());
            const list = Array.isArray(response.data) ? response.data : [];
            setTemplates(list);
            setTemplateRenameDrafts(
                Object.fromEntries(list.map(template => [template.code, template.code]))
            );
            setTemplateDescriptionDrafts(
                Object.fromEntries(list.map(template => [template.code, template.description || ""]))
            );
            setTemplateCodes(prev => prev.filter(code => list.some(template => template.code === code)));
        } catch {
            message.error("Ошибка загрузки шаблонов");
        } finally {
            setTemplatesLoading(false);
        }
    };

    React.useEffect(() => {
        loadTemplates();
    }, []);

    const loadTagRepository = React.useCallback(async () => {
        setTagRepositoryLoading(true);
        try {
            const response = await axios.get(DOCUMENT_TEMPLATE_TAGS_ENDPOINT, withAdminAuth());
            const list = Array.isArray(response.data) ? response.data : [];
            setTagRepository(list);
            setTemplateTagChecks(buildTagChecksFromRepository(templateCodes, list));
        } catch {
            message.error("Ошибка загрузки справочника тегов");
        } finally {
            setTagRepositoryLoading(false);
        }
    }, [templateCodes]);

    React.useEffect(() => {
        loadTagRepository();
    }, [loadTagRepository]);

    const loadTemplateTags = React.useCallback(async (codes = templateCodes) => {
        if (codes.length === 0) {
            setTemplateTagChecks([]);
            return;
        }

        setTemplateTagsLoading(true);
        try {
            setTemplateTagChecks(buildTagChecksFromRepository(codes, tagRepository));
        } catch {
            message.error("Ошибка проверки тегов шаблона");
        } finally {
            setTemplateTagsLoading(false);
        }
    }, [tagRepository, templateCodes]);

    React.useEffect(() => {
        loadTemplateTags(templateCodes);
    }, [loadTemplateTags, templateCodes]);

    React.useEffect(() => {
        if (templateCodes.length === 0) {
            setTemplateTagsByCode({});
            return;
        }

        let cancelled = false;

        const loadSelectedTemplateTags = async () => {
            setSelectedTemplateTagsLoading(true);
            try {
                const entries = await Promise.all(templateCodes.map(async (code) => {
                    const response = await axios.get(getTemplateTagsEndpoint(code), withAdminAuth());
                    return [code, normalizeTemplateTagsResponse(response.data)];
                }));

                if (!cancelled) {
                    setTemplateTagsByCode(Object.fromEntries(entries));
                }
            } catch {
                if (!cancelled) {
                    message.error("Ошибка загрузки тегов выбранных шаблонов");
                }
            } finally {
                if (!cancelled) {
                    setSelectedTemplateTagsLoading(false);
                }
            }
        };

        loadSelectedTemplateTags();

        return () => {
            cancelled = true;
        };
    }, [templateCodes]);

    const handleFileChange = async (event) => {
        const file = event.target.files?.[0] || null;
        setSelectedFile(file);
        setValidation(null);
        setSkippedInputFields([]);

        if (!file) return;

        try {
            const result = await validateWorkbook(file);
            setValidation(result);

            if (result.valid) {
                if (result.missingInputFields?.length > 0) {
                    Modal.warning({
                        title: "Не найдены вводные поля",
                        content: (
                            <div>
                                <p>Эти поля не найдены в первой закладке. Заполните их вручную в форме ниже.</p>
                                <ul>
                                    {result.missingInputFields.map(field => (
                                        <li key={field}>{field}</li>
                                    ))}
                                </ul>
                            </div>
                        ),
                        okText: "Заполнить вручную"
                    });
                    message.warning("Часть полей нужно заполнить вручную");
                } else if (result.tableMissing) {
                    Modal.warning({
                        title: "Табличная часть не найдена",
                        content: "Формирование документов доступно, но таблица не будет вставлена в документ.",
                        okText: "Понятно"
                    });
                    message.warning("Табличная часть не будет вставлена");
                } else {
                    message.success("Файл прошёл проверку");
                }
            } else {
                message.error("Файл не прошёл проверку");
            }
        } catch {
            setValidation({
                valid: false,
                errors: ["Не удалось прочитать Excel-файл. Проверьте формат .xlsx."],
                emptyInputFields: [],
                payload: null
            });
            message.error("Не удалось прочитать Excel-файл");
        }
    };

    const handleReset = () => {
        setSelectedFile(null);
        setValidation(null);
        setSkippedInputFields([]);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleInputFieldChange = (field, value) => {
        setValidation(prev => {
            if (!prev?.payload) return prev;

            const input = {
                ...prev.payload.input,
                [field]: value
            };

            return {
                ...prev,
                emptyInputFields: getEmptyInputFields(input, skippedInputFields, selectedTemplateFields),
                payload: {
                    ...prev.payload,
                    input
                }
            };
        });
    };

    const handleSkipInputFieldToggle = (field, checked) => {
        setSkippedInputFields(prev => {
            const next = checked
                ? prev.includes(field) ? prev : [...prev, field]
                : prev.filter(item => item !== field);

            setValidation(current => {
                if (!current?.payload) return current;

                const input = {
                    ...current.payload.input,
                    [field]: checked ? "" : current.payload.input[field]
                };

                return {
                    ...current,
                    emptyInputFields: getEmptyInputFields(input, next, selectedTemplateFields),
                    payload: {
                        ...current.payload,
                        input
                    }
                };
            });

            return next;
        });
    };

    const handleSubmit = async () => {
        if (!selectedFile || !validation?.valid || !validation.payload) {
            message.warning("Сначала выберите корректный Excel-файл");
            return;
        }

        if (templateCodes.length === 0) {
            message.warning("Выберите хотя бы один шаблон документа");
            return;
        }

        if (currentEmptyInputFields.length > 0) {
            message.warning("Заполните обязательные поля");
            return;
        }

        setSending(true);
        try {
            const input = {
                ...validation.payload.input,
                ...Object.fromEntries(selectedTemplateFields.map(field => [field, validation.payload.input[field] ?? ""]))
            };
            const response = await axios.post(DOCUMENT_ARCHIVE_ENDPOINT, {
                ...validation.payload,
                input,
                templateCodes
            }, withAdminAuth({
                responseType: "blob",
                headers: { "Content-Type": "application/json" }
            }));

            const archiveName = getFileNameFromDisposition(
                response.headers?.["content-disposition"],
                makeSafeFileName(validation.payload.input["№заявки"])
            );

            saveAs(response.data, archiveName);
            message.success("Архив документов сформирован");
        } catch {
            message.error("Ошибка формирования архива документов");
        } finally {
            setSending(false);
        }
    };

    const handleTemplateUpload = async () => {
        const code = newTemplateCode.trim();

        if (!code) {
            message.warning("Укажите код шаблона");
            return;
        }

        if (!templateFile) {
            message.warning("Выберите файл шаблона .docx");
            return;
        }

        const formData = new FormData();
        formData.append("code", code);
        formData.append("file", templateFile);

        setTemplateUploading(true);
        try {
            await axios.post(DOCUMENT_TEMPLATES_ENDPOINT, formData, withAdminAuth({
                headers: { "Content-Type": "multipart/form-data" }
            }));
            message.success("Шаблон загружен");
            setNewTemplateCode("");
            setTemplateFile(null);
            if (templateFileInputRef.current) templateFileInputRef.current.value = "";
            await loadTemplates();
            setTemplateCodes(prev => prev.includes(code) ? prev : [...prev, code]);
            setShowTagInfo(true);
            loadTemplateTags([code]);
        } catch {
            message.error("Ошибка загрузки шаблона");
        } finally {
            setTemplateUploading(false);
        }
    };

    const handleTemplateRenameDraftChange = (code, value) => {
        setTemplateRenameDrafts(prev => ({
            ...prev,
            [code]: value
        }));
    };

    const handleTemplateDescriptionDraftChange = (code, value) => {
        setTemplateDescriptionDrafts(prev => ({
            ...prev,
            [code]: value
        }));
    };

    const handleTemplateRename = async (oldCode) => {
        const newCode = String(templateRenameDrafts[oldCode] || "").trim();
        const template = templates.find(item => item.code === oldCode);
        const currentDescription = template?.description || "";
        const description = String(templateDescriptionDrafts[oldCode] ?? currentDescription).trim();

        if (!newCode) {
            message.warning("Укажите новый код шаблона");
            return;
        }

        if (newCode === oldCode && description === currentDescription) {
            message.info("Код и описание шаблона не изменились");
            return;
        }

        setTemplateActionCode(oldCode);
        try {
            await axios.put(getTemplateEndpoint(oldCode), {
                code: newCode,
                description
            }, withAdminAuth());
            message.success("Шаблон сохранён");
            await loadTemplates();
            setTemplateCodes(prev => prev.map(code => code === oldCode ? newCode : code));
        } catch {
            message.error("Ошибка переименования шаблона");
        } finally {
            setTemplateActionCode("");
        }
    };

    const handleTemplateDelete = (code) => {
        Modal.confirm({
            title: "Удалить шаблон?",
            content: `Код шаблона: ${code}`,
            okText: "Удалить",
            cancelText: "Отмена",
            okButtonProps: { danger: true },
            onOk: async () => {
                setTemplateActionCode(code);
                try {
                    await axios.delete(getTemplateEndpoint(code), withAdminAuth());
                    message.success("Шаблон удалён");
                    await loadTemplates();
                    setTemplateCodes(prev => prev.filter(templateCode => templateCode !== code));
                } catch {
                    message.error("Ошибка удаления шаблона");
                } finally {
                    setTemplateActionCode("");
                }
            }
        });
    };

    const handleTemplateDownload = async (template) => {
        setTemplateDownloadingCode(template.code);
        try {
            const response = await axios.get(getTemplateDownloadEndpoint(template.code), {
                responseType: "blob",
                headers: getAdminAuthHeaders()
            });

            const fileName = getFileNameFromDisposition(
                response.headers?.["content-disposition"],
                template.fileName || `${template.code}.docx`
            );

            saveAs(response.data, fileName);
            message.success("Шаблон скачан");
        } catch {
            message.error("Ошибка скачивания шаблона");
        } finally {
            setTemplateDownloadingCode("");
        }
    };

    const handleTemplateToggle = (code, checked) => {
        setTemplateCodes(prev => {
            if (checked) {
                return prev.includes(code) ? prev : [...prev, code];
            }

            return prev.filter(templateCode => templateCode !== code);
        });
    };

    const handlePrepareTag = (tag) => {
        const cleanTag = String(tag || "").trim();
        setNewTag(cleanTag);
        setNewTagInputField(stripTagBraces(cleanTag));
        setShowTagInfo(true);
    };

    const handleRefreshTagRepository = (event) => {
        event?.preventDefault();
        event?.stopPropagation();
        loadTagRepository();
    };

    const handleSaveTag = async () => {
        const tag = newTag.trim();
        const inputField = newTagInputField.trim();

        if (!tag) {
            message.warning("Укажите тег");
            return;
        }

        if (!inputField) {
            message.warning("Укажите поле JSON");
            return;
        }

        setTagSaving(true);
        try {
            await axios.post(DOCUMENT_TEMPLATE_TAGS_ENDPOINT, {
                tag,
                inputField
            }, withAdminAuth());
            message.success("Тег сохранён в БД");
            setNewTag("");
            setNewTagInputField("");
            setSelectedRepositoryTag("");
            await loadTagRepository();
        } catch {
            message.error("Ошибка сохранения тега");
        } finally {
            setTagSaving(false);
        }
    };

    const handleSelectRepositoryTag = (item) => {
        const tag = getRepositoryTag(item);
        setSelectedRepositoryTag(tag);
        setNewTag(tag);
        setNewTagInputField(getRepositoryField(item));
    };

    return (
        <main className={embedded ? styles.embeddedPage : styles.page}>
            <div className={styles.shell}>
                <header className={styles.header}>
                    <span className={styles.headerVisual} aria-hidden="true">
                        <FileArchive size={25} />
                    </span>
                    <div className={styles.titleBlock}>
                        <span className={styles.eyebrow}>Документы и шаблоны</span>
                        <h1 className={styles.title}>Формирование документов</h1>
                        <p className={styles.subtitle}>Загрузите Excel, проверьте первые две закладки и получите архив документов.</p>
                    </div>

                    <div className={styles.headerActions}>
                        <button
                            className={styles.headerIconButton}
                            type="button"
                            onClick={() => setTemplateSettingsOpen(true)}
                            title="Настройки шаблонов"
                            aria-label="Настройки шаблонов"
                        >
                            <Settings size={19} />
                        </button>

                        {!embedded && (
                            <Link className={styles.backLink} to="/">
                                <ArrowLeft size={16} />
                                Анализ цен
                            </Link>
                        )}
                    </div>
                </header>

                <Modal
                    open={templateSettingsOpen}
                    title={(
                        <div className={styles.templateModalTitle}>
                            <span><Settings size={20} /></span>
                            <div>
                                <strong>Шаблоны документов</strong>
                                <small>Файлы DOCX и справочник тегов</small>
                            </div>
                        </div>
                    )}
                    width={1180}
                    footer={null}
                    rootClassName={styles.templateSettingsModal}
                    onCancel={() => setTemplateSettingsOpen(false)}
                >
                    <div className={styles.templateModalContent}>
                        <p className={`${styles.panelText} ${styles.templateModalIntro}`}>
                            Управляйте сохранёнными `.docx` шаблонами, загрузкой новых файлов и справочником тегов.
                        </p>

                        {templates.length > 0 ? (
                            <div className={styles.templateTableWrapper}>
                                <table className={styles.templateTable}>
                                    <thead>
                                        <tr>
                                            <th>Код</th>
                                            <th>Описание</th>
                                            <th>Размер</th>
                                            <th>Новый код</th>
                                            <th>Новое описание</th>
                                            <th>Действия</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {templates.map(template => (
                                            <tr
                                                key={template.code}
                                                className={templateCodes.includes(template.code) ? styles.templateSelectedRow : ""}
                                            >
                                                <td>
                                                    <strong>{template.code}</strong>
                                                </td>
                                                <td>{getTemplateDescription(template)}</td>
                                                <td>{template.size ? `${template.size} байт` : ""}</td>
                                                <td>
                                                    <input
                                                        className={styles.tableInput}
                                                        value={templateRenameDrafts[template.code] ?? template.code}
                                                        onChange={event => handleTemplateRenameDraftChange(template.code, event.target.value)}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        className={styles.tableInput}
                                                        placeholder="Описание шаблона"
                                                        value={templateDescriptionDrafts[template.code] ?? template.description ?? ""}
                                                        onChange={event => handleTemplateDescriptionDraftChange(template.code, event.target.value)}
                                                    />
                                                </td>
                                                <td>
                                                    <div className={styles.tableActions}>
                                                        <button
                                                            className={styles.secondaryButton}
                                                            type="button"
                                                            disabled={templateActionCode === template.code}
                                                            onClick={() => handleTemplateRename(template.code)}
                                                        >
                                                            Сохранить
                                                        </button>
                                                        <button
                                                            className={styles.secondaryButton}
                                                            type="button"
                                                            disabled={templateActionCode === template.code || templateDownloadingCode === template.code}
                                                            onClick={() => handleTemplateDownload(template)}
                                                        >
                                                            {templateDownloadingCode === template.code ? "Скачивание..." : "Скачать"}
                                                        </button>
                                                        <button
                                                            className={styles.dangerButton}
                                                            type="button"
                                                            disabled={templateActionCode === template.code}
                                                            onClick={() => handleTemplateDelete(template.code)}
                                                        >
                                                            Удалить
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <p className={styles.panelText}>Список шаблонов пуст.</p>
                        )}

                        <div className={styles.templateUploadBlock}>
                            <h3 className={styles.subPanelTitle}>Загрузить новый шаблон</h3>

                            <div className={styles.templateUploadGrid}>
                                <div>
                                    <label className={styles.fieldLabel} htmlFor="new-template-code">Код шаблона</label>
                                    <input
                                        id="new-template-code"
                                        className={styles.fieldInput}
                                        placeholder="goz_contract"
                                        value={newTemplateCode}
                                        onChange={event => setNewTemplateCode(event.target.value)}
                                    />
                                </div>

                                <div>
                                    <label className={styles.fieldLabel} htmlFor="new-template-file">Файл шаблона</label>
                                    <input
                                        id="new-template-file"
                                        ref={templateFileInputRef}
                                        className={styles.fileInput}
                                        type="file"
                                        accept=".docx"
                                        onChange={event => setTemplateFile(event.target.files?.[0] || null)}
                                    />
                                </div>
                            </div>

                            <div className={styles.actions}>
                                <button
                                    className={styles.secondaryButton}
                                    type="button"
                                    disabled={templateUploading}
                                    onClick={handleTemplateUpload}
                                >
                                    {templateUploading ? "Загрузка..." : "Загрузить шаблон"}
                                </button>

                                <button
                                    className={styles.secondaryButton}
                                    type="button"
                                    disabled={templatesLoading}
                                    onClick={loadTemplates}
                                >
                                    Обновить список
                                </button>

                                <button
                                    className={styles.secondaryButton}
                                    type="button"
                                    disabled={templateTagsLoading || templateCodes.length === 0}
                                    onClick={() => loadTemplateTags()}
                                >
                                    {templateTagsLoading ? "Проверка..." : "Проверить теги выбранных"}
                                </button>
                            </div>
                        </div>

                        <div className={styles.collapsibleHeader}>
                            <h3 className={styles.subPanelTitle}>Информация по тегам</h3>
                            <button
                                className={styles.iconToggleButton}
                                type="button"
                                aria-expanded={showTagInfo}
                                onClick={() => setShowTagInfo(prev => !prev)}
                                title={showTagInfo ? "Скрыть информацию по тегам" : "Показать информацию по тегам"}
                            >
                                {showTagInfo ? "−" : "+"}
                            </button>
                        </div>

                        {showTagInfo && (
                            <div className={styles.mappingGrid}>
                                <section className={styles.mappingPanel}>
                                    <h3 className={styles.subPanelTitle}>Маппинг вводных</h3>
                                    <div className={styles.mappingTableWrapper}>
                                        <table className={styles.mappingTable}>
                                            <thead>
                                                <tr>
                                                    <th>Поле JSON</th>
                                                    <th>Тег в шаблоне</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {COMMON_TAG_MAPPING.map(item => (
                                                    <tr key={item.inputField}>
                                                        <td>{`input["${item.inputField}"]`}</td>
                                                        <td><code>{item.tag}</code></td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </section>

                                <section className={styles.mappingPanel}>
                                    <h3 className={styles.subPanelTitle}>Проверка тегов по справочнику БД</h3>

                                    {templateCodes.length === 0 && (
                                        <p className={styles.panelText}>Выберите шаблон, чтобы увидеть проверку тегов.</p>
                                    )}

                                    {templateTagChecks.length > 0 && (
                                        <div className={styles.tagCheckList}>
                                            {templateTagChecks.map(check => (
                                                <div className={styles.tagCheckCard} key={check.templateCode || JSON.stringify(check)}>
                                                    <strong>{check.templateCode || "Шаблон"}</strong>

                                                    <div className={styles.tagGroup}>
                                                        <span className={styles.tagGroupTitle}>Теги в БД</span>
                                                        <span>{(check.knownTags || []).join(", ") || "нет"}</span>
                                                    </div>

                                                    <div className={styles.tagGroup}>
                                                        <span className={styles.tagGroupTitle}>Неизвестные теги</span>
                                                        {(check.unknownTags || []).length ? (
                                                            <span className={styles.unknownTagList}>
                                                                {check.unknownTags.map(tag => (
                                                                    <button
                                                                        className={styles.tagPillButton}
                                                                        key={tag}
                                                                        type="button"
                                                                        onClick={() => handlePrepareTag(tag)}
                                                                        title="Добавить тег в справочник"
                                                                    >
                                                                        {tag}
                                                                    </button>
                                                                ))}
                                                            </span>
                                                        ) : (
                                                            <span className={styles.statusOk}>нет</span>
                                                        )}
                                                    </div>

                                                    <div className={styles.tagGroup}>
                                                        <span className={styles.tagGroupTitle}>Популярные поля не в БД</span>
                                                        <span>{(check.missingCommonTags || []).join(", ") || "нет"}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </section>

                                <section className={styles.mappingPanel}>
                                    <h3 className={styles.subPanelTitle}>Справочник тегов в БД</h3>

                                    <div className={styles.actions}>
                                        <button
                                            className={styles.secondaryButton}
                                            type="button"
                                            disabled={tagRepositoryLoading}
                                            onClick={handleRefreshTagRepository}
                                        >
                                            {tagRepositoryLoading ? "Обновление..." : "Обновить справочник"}
                                        </button>
                                    </div>

                                    <div className={styles.tagFormGrid}>
                                        <div>
                                            <label className={styles.fieldLabel} htmlFor="new-db-tag">Тег</label>
                                            <input
                                                id="new-db-tag"
                                                className={styles.fieldInput}
                                                placeholder="{{сумма}}"
                                                value={newTag}
                                                readOnly={!!selectedRepositoryTag}
                                                onChange={event => setNewTag(event.target.value)}
                                            />
                                        </div>

                                        <div>
                                            <label className={styles.fieldLabel} htmlFor="new-db-tag-field">Поле JSON</label>
                                            <input
                                                id="new-db-tag-field"
                                                className={styles.fieldInput}
                                                placeholder="сумма"
                                                value={newTagInputField}
                                                onChange={event => setNewTagInputField(event.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className={styles.actions}>
                                        <button
                                            className={styles.secondaryButton}
                                            type="button"
                                            disabled={tagSaving || !newTag.trim() || !newTagInputField.trim()}
                                            onClick={handleSaveTag}
                                        >
                                            {tagSaving ? "Сохранение..." : selectedRepositoryTag ? "Обновить выбранный тег" : "Добавить тег"}
                                        </button>

                                        <button
                                            className={styles.secondaryButton}
                                            type="button"
                                            disabled={!selectedRepositoryTag && !newTag && !newTagInputField}
                                            onClick={() => {
                                                setSelectedRepositoryTag("");
                                                setNewTag("");
                                                setNewTagInputField("");
                                            }}
                                        >
                                            Сбросить выбор
                                        </button>
                                    </div>

                                    {tagRepository.length > 0 ? (
                                        <div className={styles.mappingTableWrapper}>
                                            <table className={styles.mappingTable}>
                                                <thead>
                                                    <tr>
                                                        <th>Тег</th>
                                                        <th>Поле JSON</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {tagRepository.map((item, index) => (
                                                        <tr
                                                            key={`${item.tag || item.name || index}-${index}`}
                                                            className={selectedRepositoryTag === getRepositoryTag(item) ? styles.templateSelectedRow : ""}
                                                            onClick={() => handleSelectRepositoryTag(item)}
                                                        >
                                                            <td><code>{getRepositoryTag(item)}</code></td>
                                                            <td>{getRepositoryField(item)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <p className={styles.panelText}>Справочник тегов пока пуст.</p>
                                    )}
                                </section>
                            </div>
                        )}
                    </div>
                </Modal>

                <div className={styles.grid}>
                    <section className={`${styles.panel} ${styles.fullWidthPanel}`}>
                        <div className={styles.panelHeading}>
                            <span className={styles.panelIcon}><ClipboardCheck size={20} /></span>
                            <div>
                                <h2 className={styles.panelTitle}>Проверка файла</h2>
                                <p className={styles.panelText}>Проверка структуры, вводных полей и выбранных шаблонов.</p>
                            </div>
                        </div>

                        {!validation && (
                            <p className={styles.panelText}>Выберите файл, чтобы увидеть результат проверки.</p>
                        )}

                        {validation && (
                            <ul className={styles.statusList}>
                                <li className={styles.statusItem}>
                                    <span>Первая закладка с вводными</span>
                                    <span className={validation.valid ? styles.statusOk : styles.statusError}>
                                        {validation.payload?.sheets.input || "Ошибка"}
                                    </span>
                                </li>
                                <li className={styles.statusItem}>
                                    <span>Вторая закладка с табличной частью</span>
                                    <span className={validation.tableMissing ? styles.statusWarning : validation.valid ? styles.statusOk : styles.statusError}>
                                        {validation.tableMissing
                                            ? "Не будет вставлена"
                                            : validation.payload?.sheets.items || "Ошибка"}
                                    </span>
                                </li>
                                <li className={styles.statusItem}>
                                    <span>Вводные поля</span>
                                    <span className={currentEmptyInputFields.length ? styles.statusError : currentMissingInputFields.length ? styles.statusWarning : styles.statusOk}>
                                        {currentEmptyInputFields.length
                                            ? "Заполнить или пропустить"
                                            : currentMissingInputFields.length ? "Готово с пропусками" : "Найдены"}
                                    </span>
                                </li>
                                <li className={styles.statusItem}>
                                    <span>Таблица в документе</span>
                                    <span className={validation.tableMissing ? styles.statusWarning : styles.statusOk}>
                                        {validation.tableMissing ? "Не будет вставлена" : `${validation.payload?.items?.length || 0} строк`}
                                    </span>
                                </li>
                            </ul>
                        )}

                        {validation?.errors?.length > 0 && (
                            <div className={styles.errorBox}>
                                {validation.errors.map(error => (
                                    <span key={error}>{error}</span>
                                ))}
                            </div>
                        )}

                        {validation?.warnings?.length > 0 && (
                            <div className={styles.warningBox}>
                                {validation.warnings.map(warning => (
                                    <span key={warning}>{warning}</span>
                                ))}
                            </div>
                        )}

                        {summary && (
                            <>
                                <div className={styles.summaryGrid}>
                                    <div className={styles.summaryCard}>
                                        <p className={styles.summaryLabel}>Шаблоны документов</p>
                                        <div className={styles.templateChoiceTableWrapper}>
                                            {templates.length > 0 ? (
                                                <table className={styles.templateChoiceTable}>
                                                    <thead>
                                                        <tr>
                                                            <th></th>
                                                            <th>Код</th>
                                                            <th>Описание</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {templates.map(template => (
                                                            <tr
                                                                key={template.code}
                                                                className={templateCodes.includes(template.code) ? styles.templateSelectedRow : ""}
                                                            >
                                                                <td>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={templateCodes.includes(template.code)}
                                                                        disabled={templatesLoading}
                                                                        onChange={event => handleTemplateToggle(template.code, event.target.checked)}
                                                                        aria-label={`Выбрать шаблон ${template.code}`}
                                                                    />
                                                                </td>
                                                                <td><strong>{template.code}</strong></td>
                                                                <td>{getTemplateDescription(template)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            ) : (
                                                <span className={styles.summaryHint}>Шаблоны не загружены. Добавьте их через шестерёнку в хедере.</span>
                                            )}
                                        </div>
                                        <span className={styles.summaryHint}>
                                            {selectedTemplates.length > 0
                                                ? selectedTemplates.map(getTemplateDescription).join(", ")
                                                : "Выберите хотя бы один шаблон"}
                                        </span>
                                    </div>
                                </div>

                                <div className={styles.fieldGroups}>
                                    {selectedTemplateTagsLoading && (
                                        <div className={styles.warningBox}>Загружаю поля выбранных шаблонов...</div>
                                    )}

                                    {!selectedTemplateTagsLoading && templateCodes.length === 0 && (
                                        <div className={styles.warningBox}>Выберите шаблон документа, чтобы увидеть нужные поля.</div>
                                    )}

                                    {!selectedTemplateTagsLoading && templateCodes.length > 0 && selectedTemplateFields.length === 0 && (
                                        <div className={styles.warningBox}>В выбранных шаблонах не найдены теги вводных полей.</div>
                                    )}

                                    {groupedSelectedFields.map(group => (
                                        <section className={styles.fieldGroup} key={group.title}>
                                            <h3 className={styles.fieldGroupTitle}>{group.title}</h3>
                                            <div className={styles.fields}>
                                                {group.fields.map(field => (
                                                    <div
                                                        className={`${styles.field} ${skippedInputFields.includes(field) ? styles.fieldSkipped : ""}`}
                                                        key={field}
                                                    >
                                                        <label className={styles.fieldLabel} htmlFor={`archive-field-${field}`}>
                                                            <span>
                                                                {field}
                                                                {getFieldHint(field) && (
                                                                    <span className={styles.fieldAutoHint}>{getFieldHint(field)}</span>
                                                                )}
                                                            </span>
                                                            <code className={styles.fieldTag}>{getTagForInputField(field, tagRepository)}</code>
                                                        </label>
                                                        <input
                                                            id={`archive-field-${field}`}
                                                            type={isDateField(field) ? "date" : "text"}
                                                            disabled={skippedInputFields.includes(field)}
                                                            className={`${styles.fieldInput} ${isBlank(validation.payload.input[field]) && !skippedInputFields.includes(field) ? styles.fieldInvalid : ""}`}
                                                            value={isDateField(field)
                                                                ? toDateInputValue(validation.payload.input[field])
                                                                : String(validation.payload.input[field] ?? "")}
                                                            onChange={event => handleInputFieldChange(
                                                                field,
                                                                isDateField(field) ? fromDateInputValue(event.target.value) : event.target.value
                                                            )}
                                                        />
                                                        <label className={styles.skipFieldToggle}>
                                                            <input
                                                                type="checkbox"
                                                                checked={skippedInputFields.includes(field)}
                                                                onChange={event => handleSkipInputFieldToggle(field, event.target.checked)}
                                                            />
                                                            <span>не заполнять</span>
                                                        </label>
                                                    </div>
                                                ))}
                                            </div>
                                        </section>
                                    ))}
                                </div>
                            </>
                        )}
                    </section>

                    <section className={`${styles.panel} ${styles.fullWidthPanel}`}>
                        <div className={styles.panelHeading}>
                            <span className={`${styles.panelIcon} ${styles.panelIconSuccess}`}><Upload size={20} /></span>
                            <div>
                                <h2 className={styles.panelTitle}>Загрузить файл с данными</h2>
                                <p className={styles.panelText}>
                                    Ожидаются первая закладка с вводными параметрами и вторая закладка с табличной частью.
                                </p>
                            </div>
                        </div>

                        <input
                            ref={fileInputRef}
                            className={styles.fileInput}
                            type="file"
                            accept=".xlsx,.xls"
                            onChange={handleFileChange}
                        />

                        <div className={styles.actions}>
                            <button
                                className={styles.primaryButton}
                                type="button"
                                disabled={!validation?.valid || currentEmptyInputFields.length > 0 || templateCodes.length === 0 || selectedTemplateTagsLoading || sending}
                                onClick={handleSubmit}
                            >
                                {sending ? "Формирование..." : "Сформировать архив"}
                            </button>

                            <button
                                className={styles.secondaryButton}
                                type="button"
                                disabled={!selectedFile || sending}
                                onClick={handleReset}
                            >
                                Очистить
                            </button>
                        </div>
                    </section>
                </div>
            </div>
        </main>
    );
}
